"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _airbnbPropTypes = _interopRequireDefault(require("airbnb-prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _ToggleSwitch = _interopRequireDefault(require("./ToggleSwitch"));
var _Fieldset = _interopRequireDefault(require("../Fieldset"));
var _StackView = require("../StackView");
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);
const [selectItemProps, selectedItemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.viewProps]);
const ToggleSwitchGroup = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    copy = 'en',
    variant,
    tokens,
    items = [],
    values,
    initialValues,
    maxValues = 1,
    onChange,
    readOnly = false,
    inactive = false,
    feedback,
    hint,
    hintPosition = 'inline',
    tooltip,
    legend,
    name: inputGroupName,
    accessibilityRole = maxValues === 1 ? 'radiogroup' // radiogroup is cross-platform; only web aria has generic groups
    : _Platform.default.select({
      web: 'group',
      default: 'none'
    }),
    toggleSwitchTokens,
    validation,
    ...rest
  } = _ref;
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    space,
    fieldSpace
  } = (0, _ThemeProvider.useThemeTokens)('ToggleSwitchGroup', tokens, variant, {
    viewport
  });
  const {
    currentValues,
    toggleOneValue
  } = (0, _utils.useMultipleInputValues)({
    initialValues,
    values,
    maxValues,
    onChange,
    readOnly
  });
  const selectedProps = selectProps({
    accessibilityRole,
    ...rest
  });
  const itemA11yRole = selectedProps.accessibilityRole === 'radiogroup' ? 'radio' : 'switch';
  const uniqueFields = ['id', 'label'];
  if (!(0, _utils.containUniqueFields)(items, uniqueFields)) {
    throw new Error(`ToggleSwitchGroup items must have unique ${uniqueFields.join(', ')}`);
  }
  const toggleSwitches = items.map((_ref2, index) => {
    let {
      label,
      id = label,
      accessibilityLabel = label,
      onChange: itemOnChange,
      ref: itemRef,
      tooltip: itemTooltip,
      togglePosition,
      ...itemRest
    } = _ref2;
    const isSelected = currentValues.includes(id);
    const handleChange = (newCheckedState, event) => {
      if (typeof itemOnChange === 'function') itemOnChange(newCheckedState, event);
      toggleOneValue(id, event);
    };
    const itemA11y = {
      accessibilityState: {
        checked: isSelected
      },
      accessibilityRole: itemA11yRole,
      accessibilityLabel,
      ..._utils.a11yProps.getPositionInSet(items.length, index)
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_ToggleSwitch.default, {
      copy: copy,
      id: id,
      ref: itemRef,
      onChange: handleChange,
      tokens: toggleSwitchTokens,
      value: isSelected,
      inactive: inactive,
      label: label,
      tooltip: itemTooltip,
      togglePosition: togglePosition,
      ...itemA11y,
      ...selectItemProps(itemRest)
    }, id);
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Fieldset.default, {
    ref: ref,
    name: inputGroupName,
    legend: legend,
    tooltip: tooltip,
    hint: hint,
    hintPosition: hintPosition,
    space: fieldSpace,
    feedback: feedback,
    inactive: inactive,
    validation: validation,
    ...selectedProps,
    children: (0, _StackView.getStackedContent)(toggleSwitches, {
      space,
      direction: 'column'
    })
  });
});
ToggleSwitchGroup.displayName = 'ToggleSwitchGroup';
ToggleSwitchGroup.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: _propTypes.default.oneOf(['en', 'fr']),
  tokens: (0, _utils.getTokensPropType)('ToggleSwitchGroup'),
  variant: _utils.variantProp.propType,
  /**
   * The maximum number of items a user may select at once. Defaults to 1 and behaves
   * like radio buttons. To have no limit and allow any number of selections, pass `null`.
   */
  maxValues: _propTypes.default.number,
  /**
   * The options a user may select
   */
  items: _propTypes.default.arrayOf(_propTypes.default.shape({
    ...selectedItemPropTypes,
    /**
     * The text displayed to the user on the label.
     */
    label: _propTypes.default.string.isRequired,
    /**
     * An optional accessibility label may be passed to each ToggleSwitch
     * and will be applied as normal for a React Native accessibilityLabel prop.
     */
    accessibilityLabel: _propTypes.default.string,
    /**
     * An optional unique string may be provided to identify this option,
     * which will be used in code and passed to any onChange function.
     * If not provided, the label is used.
     */
    id: _propTypes.default.string,
    /**
     * An optional ref for one individual ToggleSwitch in the ToggleSwitchGroup
     */
    ref: _airbnbPropTypes.default.ref(),
    /**
     * Controls the position of the switch in relation to the label.
     * - 'start': switch on the start, before the `label` and `tooltip`.
     * - 'end': switch to the end, after the `label` and `tooltip` (default).
     */
    togglePosition: _propTypes.default.oneOf(['start', 'end'])
  })),
  /**
   * If provided, this function is called when the current selection is changed
   * and is passed an array of the `id`s of all currently selected `items`.
   */
  onChange: _propTypes.default.func,
  /**
   * If the selected item(s) in the toggle switch group are to be controlled externally by
   * a parent component, pass an array of strings as well as an `onChange` handler.
   * Passing an array for "values" makes the ToggleSwitchGroup a "controlled" component that
   * expects its state to be handled via `onChange` and so doesn't handle it itself.
   */
  values: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * If `values` is not passed, making the ToggleSwitchGroup an "uncontrolled" component
   * managing its own selected state, a default set of selections may be provided.
   * Changing the `initialValues` does not change the user's selections.
   */
  initialValues: _propTypes.default.arrayOf(_propTypes.default.string),
  /**
   * Optional additional text giving more detail to help a user make a choice.
   */
  hint: _propTypes.default.string,
  /**
   * Position of the hint relative to label. Use `below` to display a larger hint below the label.
   */
  hintPosition: _propTypes.default.oneOf(['inline', 'below']),
  /**
   * Optional tooltip text content to include alongside the legend and hint.
   */
  tooltip: _propTypes.default.string,
  /**
   * If provided, a Feedback element is rendered containing this text.
   */
  feedback: _propTypes.default.string,
  /**
   * Main text used to describe this group, used in Fieldset's Legend element.
   */
  legend: _propTypes.default.string,
  /**
   * Toggle switch token overrides.
   */
  toggleSwitchTokens: (0, _utils.getTokensPropType)('ToggleSwitch'),
  /**
   * Current validation status of the group, passed to the feedback element if there is one.
   */
  validation: _propTypes.default.oneOf(['error', 'success'])
};
var _default = exports.default = ToggleSwitchGroup;